import { AppHTTPService } from '@/api/http-service'
import { useAuth } from '@/auth/AuthProvider'
import Header from '@/components/Header/Header'
import { EnrichmentEventsProvider } from '@/contexts/EnrichmentEventsContext'
import { FieldsContextProvider } from '@/contexts/FieldsContext'
import { useRulesContext } from '@/contexts/RulesContext'
import { SheetProvider } from '@/contexts/SheetContext'
import { TableProviders } from '@flatfile/turntable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChatSheet } from './chat.sheet'
import './page.module.css'

export default function PrepareChatLayout() {
  const { addPendingRules } = useRulesContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  // State management
  const { projectId, fileId, sheetId, artifactId } = useParams()
  const [searchParams] = useSearchParams()

  const { data: projectsResponse, isLoading: projectsIsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await AppHTTPService.client.projects.list()
      return response
    },
    enabled: isAuthenticated && !authLoading, // Only run when authenticated and not loading
  })

  // If no projectId is defined, use localStorage to find the last used projectId
  useEffect(() => {
    if (projectId) {
      localStorage.setItem('lastUsedProjectId', projectId)
      return
    }
    const lastUsedProjectId = localStorage.getItem('lastUsedProjectId')
    if (lastUsedProjectId) {
      navigate(`/projects/${lastUsedProjectId}/prepare-chat`, { replace: true })
      return
    }
    if (!projectsIsLoading && projectsResponse && projectsResponse.length > 0) {
      const firstProjectId = projectsResponse[0].id
      navigate(`/projects/${firstProjectId}/prepare-chat`, { replace: true })
    }
  }, [projectId, navigate, projectsResponse, projectsIsLoading])

  const { data: threadsResponse } = useQuery({
    queryKey: ['threads', projectId],
    queryFn: async () => {
      const response = await AppHTTPService.client.threads.list(projectId)
      return response
    },
    enabled: !!projectId,
  })

  // Create a thread if none exists
  const createThreadMutation = useMutation({
    mutationFn: async () => {
      if (!projectId) return null
      return AppHTTPService.client.threads.create(
        '', // sourceSheetId - empty for now
        {}, // metadata
        projectId,
        'chat'
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['threads', projectId] })
    },
  })

  const threadId = useMemo(() => {
    if (
      threadsResponse &&
      threadsResponse.length === 0 &&
      projectId &&
      !createThreadMutation.isPending
    ) {
      // No threads exist, create one
      createThreadMutation.mutate()
    }

    const filteredThreads = threadsResponse?.filter(
      (thread: any) => thread.type === 'chat'
    )

    return filteredThreads?.[0]?.id
  }, [threadsResponse, projectId, createThreadMutation])

  const { data: threadResponse } = useQuery({
    queryKey: ['thread', threadId],
    queryFn: async () => {
      const response = await AppHTTPService.client.threads.get(threadId)
      return response.data
    },
    enabled: !!threadId,
  })

  const { data: filesResponse } = useQuery({
    queryKey: ['files', projectId],
    queryFn: () => AppHTTPService.client.files.list(projectId),
    enabled: !!projectId && isAuthenticated && !authLoading,
  })

  const { data: artifactsResponse, refetch: refetchArtifacts } = useQuery({
    queryKey: ['artifacts', projectId],
    queryFn: () => AppHTTPService.client.artifacts.list({ projectId }),
    enabled: !!projectId && isAuthenticated && !authLoading,
  })

  useEffect(() => {
    // Skip navigation if an artifact is selected
    if (artifactId) return

    if (!fileId && filesResponse?.data?.length > 0) {
      const fileId = filesResponse?.data?.[0]?.id
      navigate(`/projects/${projectId}/files/${fileId}/prepare-chat`)
    }
  }, [filesResponse, artifactId, fileId, projectId, navigate])

  const files = useMemo(() => {
    return filesResponse?.data
  }, [filesResponse])

  const artifacts = useMemo(() => {
    return artifactsResponse
  }, [artifactsResponse])

  const selectedArtifact = useMemo(() => {
    if (!artifactId || !artifacts) return null
    return artifacts.find((a: any) => a.id === artifactId)
  }, [artifactId, artifacts])

  const { data: sheetsResponse, isLoading: sheetsIsLoading } = useQuery({
    queryKey: ['sheets', fileId ?? files?.[0]?.id],
    queryFn: () => AppHTTPService.client.sheets.list(fileId ?? files?.[0]?.id),
    enabled: (!!fileId || !!files?.[0]?.id) && isAuthenticated && !authLoading,
  })

  useEffect(() => {
    // Skip navigation if an artifact is selected
    if (artifactId) return

    if (!sheetId && sheetsResponse?.data?.length > 0) {
      const sheetId = sheetsResponse.data[0].id
      navigate(
        `/projects/${projectId}/files/${fileId}/sheets/${sheetId}/prepare-chat`
      )
    }
  }, [sheetsResponse, artifactId, sheetId, projectId, fileId, navigate])

  const { data: currentSheetData } = useQuery({
    queryKey: ['sheet', sheetId],
    queryFn: () => AppHTTPService.client.sheets.get(sheetId!),
    enabled: !!sheetId && isAuthenticated && !authLoading,
  })

  useEffect(() => {
    if (currentSheetData?.data?.rules) {
      // Use rules from the current sheet
      const { operations } = threadResponse?.state || { operations: [] }

      const revertOperations = operations
        .filter((op) => op.toolName === 'revert')
        .map((op) => op.targetOperationId)

      const filteredRules = currentSheetData.data.rules?.filter(
        (rule) =>
          !revertOperations.includes(rule.toolId) && rule.disabled !== true
      )

      addPendingRules(filteredRules)
    }
  }, [currentSheetData, threadResponse, addPendingRules])

  const projects = useMemo(() => {
    return projectsResponse?.map((project: any) => ({
      id: project.id,
      name: project.name || project.id,
    }))
  }, [projectsResponse])

  async function handleRenameFile(fileId: string, newName: string) {
    await AppHTTPService.client.files.update(fileId, { name: newName })
    // Invalidate and refetch files list
    if (projectId) {
      await queryClient.invalidateQueries({ queryKey: ['files', projectId] })
    }
  }

  return (
    <TableProviders tableId='sheet-table'>
      <SheetProvider>
        <EnrichmentEventsProvider>
          <FieldsContextProvider>
            <div className='w-full max-w-full m-0 p-0 font-sans text-gray-800 bg-white flex flex-col'>
              <div className='flex flex-col h-screen bg-white relative'>
                <Header
                  files={files}
                  artifacts={artifacts}
                  sessions={projects}
                  // onRenameFile={handleRenameFile}
                />
                {/* Main content area with chat and sheet view */}
                <div className='flex flex-1 max-h-[calc(100%-50px)]'>
                  {threadId && (fileId || artifactId) ? (
                    <ChatSheet
                      key={selectedArtifact?.updatedAt || 'no-artifact'}
                      sourceSheetId={sheetId || ''}
                      fileId={fileId || ''}
                      threadId={threadId}
                      thread={threadResponse}
                      hasNoSheets={
                        !sheetsIsLoading &&
                        !artifactId &&
                        (!sheetsResponse?.data ||
                          sheetsResponse.data.length === 0)
                      }
                      projectId={projectId}
                      artifact={selectedArtifact}
                    />
                  ) : (
                    <div className='flex-1 flex items-center justify-center'>
                      <div className='text-gray-500'>Loading...</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </FieldsContextProvider>
        </EnrichmentEventsProvider>
      </SheetProvider>
    </TableProviders>
  )
}
